import { Controller } from '@hotwired/stimulus'
import { formatPrice } from '@/utils/functions'
import InputController from '@/public/components/Input/controller'

export default class extends Controller {
    static values = {
        currentPrice: Number,
        step: Number,
    }

    private inputController: InputController | null = null
    private currentPrice: number = 0
    private step: number = 1000

    declare readonly currentPriceValue: number
    declare readonly hasCurrentPriceValue: boolean
    declare readonly stepValue: number
    declare readonly hasStepValue: boolean

    connect() {
        this.currentPrice = this.hasCurrentPriceValue ? this.currentPriceValue : 0
        this.step = this.hasStepValue ? this.stepValue : 1000

        queueMicrotask(() => this.setupInputController())
    }

    private setupInputController(): void {
        this.inputController = this.application.getControllerForElementAndIdentifier(
            this.element,
            'input'
        ) as InputController | null

        if (!this.inputController) {
            return
        }

        const input = this.inputController.getInput()

        if (!input) {
            return
        }

        input.addEventListener('input', this.handleInput as EventListener)
        input.addEventListener('keydown', this.handleKeyDown as EventListener)
        input.addEventListener('blur', this.handleBlur as EventListener)

        this.upsertInitialValue()
    }

    disconnect() {
        if (!this.inputController) {
            return
        }

        const input = this.inputController.getInput()

        if (!input) {
            return
        }

        input.removeEventListener('input', this.handleInput as EventListener)
        input.removeEventListener('keydown', this.handleKeyDown as EventListener)
        input.removeEventListener('blur', this.handleBlur as EventListener)
    }

    increase(event: Event) {
        event.preventDefault()

        if (!this.inputController) {
            return
        }

        const value = this.extractNumber(this.inputController.getValue())
        const newValue = this.increaseValue(value)

        this.setValue(newValue)
    }

    decrease(event: Event) {
        event.preventDefault()

        if (!this.inputController) {
            return
        }

        const value = this.extractNumber(this.inputController.getValue())
        const newValue = this.decreaseValue(value)

        this.setValue(newValue)
    }

    getNumericValue(): number {
        if (!this.inputController) {
            return 0
        }

        return this.extractNumber(this.inputController.getValue())
    }

    setValue(price: number): void {
        if (!this.inputController) {
            return
        }

        this.inputController.setValue(formatPrice(price))
        this.dispatchChange(price)
    }

    setCurrentPrice(value: number) {
        this.currentPrice = value
        this.upsertInitialValue()
    }

    private upsertInitialValue(): void {
        if (this.currentPrice > 0) {
            this.setValue(this.currentPrice + this.step)
        }
    }

    private extractNumber(text: string): number {
        return Number((text || '').replace(/\D/g, '') || 0)
    }

    private resolveCaretPosition(previousValue: string, previousPosition: number, newValue: string): number {
        const max = this.getEditableMax(newValue)
        const leftDigits = (previousValue.slice(0, previousPosition).match(/\d/g) || []).length

        if (leftDigits <= 0) {
            let position = 0

            while (position < max && /\D/.test(newValue[position])) {
                position++
            }

            return position
        }

        let position = 0
        let seen = 0

        for (let i = 0; i < newValue.length; i++) {
            const char = newValue[i]

            if (/\d/.test(char)) {
                seen++
                position = i + 1

                if (seen >= leftDigits) {
                    break
                }
            }
        }

        if (seen < leftDigits) {
            position = max
        }

        if (position > max) {
            position = max
        }

        while (position < max && /\D/.test(newValue[position])) {
            position++
        }

        return position
    }

    private getEditableMax(value: string): number {
        return Math.max(0, value.length - 2)
    }

    private moveRight(position: number, value: string): number {
        const max = this.getEditableMax(value)
        let i = Math.min(position + 1, max)

        while (i < max && /\D/.test(value[i])) {
            i++
        }

        return Math.min(i, max)
    }

    private moveLeft(pos: number, value: string): number {
        let i = Math.max(pos - 1, 0)

        while (i > 0 && /\D/.test(value[i - 1])) {
            i--
        }

        return i
    }

    private digitsBefore(value: string, pos: number): number {
        return (value.slice(0, pos).match(/\d/g) || []).length
    }

    private digitsInRange(value: string, from: number, to: number): number {
        return (value.slice(from, to).match(/\d/g) || []).length
    }

    private removeDigitAt(digits: string, index: number): string {
        if (index < 0 || index >= digits.length) {
            return digits
        }

        return digits.slice(0, index) + digits.slice(index + 1)
    }

    private formatFromDigits(digits: string): string {
        return formatPrice(Number(digits || '0'))
    }

    private caretPosForLeftDigits(leftDigits: number, formatted: string): number {
        const max = this.getEditableMax(formatted)

        if (leftDigits <= 0) {
            let position = 0

            while (position < max && /\D/.test(formatted[position])) {
                position++
            }

            return position
        }

        let seen = 0
        let position = 0

        for (let i = 0; i < formatted.length; i++) {
            if (!/\d/.test(formatted[i])) {
                seen++
                position = i + 1

                if (seen >= leftDigits) {
                    break
                }
            }
        }

        if (seen < leftDigits) {
            position = max
        }

        if (position > max) {
            position = max
        }

        while (position < max && /\D/.test(formatted[position])) {
            position++
        }

        return position
    }

    private deleteBackspace(value: string, start: number, end: number): { formatted: string, leftDigits: number } {
        const digits = (value.replace(/\D/g, ''))

        if (start !== end) {
            const left = this.digitsBefore(value, start)
            const count = this.digitsInRange(value, start, end)
            const nextDigits = digits.slice(0, left) + digits.slice(left + count)
            const formatted = this.formatFromDigits(nextDigits)

            return { formatted, leftDigits: left }
        }

        const left = this.digitsBefore(value, start)

        if (left <= 0) {
            return { formatted: this.formatFromDigits(digits), leftDigits: 0 }
        }

        const nextDigits = this.removeDigitAt(digits, left - 1)
        const formatted = this.formatFromDigits(nextDigits)

        return { formatted, leftDigits: left - 1 }
    }

    private deleteForward(value: string, start: number, end: number): { formatted: string, leftDigits: number } {
        const digits = (value.replace(/\D/g, ''))

        if (start !== end) {
            const left = this.digitsBefore(value, start)
            const count = this.digitsInRange(value, start, end)
            const nextDigits = digits.slice(0, left) + digits.slice(left + count)
            const formatted = this.formatFromDigits(nextDigits)

            return { formatted, leftDigits: left }
        }

        const left = this.digitsBefore(value, start)
        const total = digits.length

        if (left >= total) {
            return { formatted: this.formatFromDigits(digits), leftDigits: left }
        }

        const nextDigits = this.removeDigitAt(digits, left)
        const formatted = this.formatFromDigits(nextDigits)

        return { formatted, leftDigits: left }
    }

    private adjustToStep(entered: number): number {
        if (entered <= this.currentPrice) {
            return this.currentPrice + this.step
        }

        const remainder = (entered - this.currentPrice) % this.step

        if (remainder === 0) {
            return entered
        }

        return Math.ceil((entered - this.currentPrice) / this.step) * this.step + this.currentPrice
    }

    private increaseValue(value: number): number {
        if (value + this.step < this.currentPrice) {
            return this.currentPrice + this.step
        }

        return value + this.step
    }

    private decreaseValue(value: number): number {
        if (value <= this.currentPrice || value - this.step <= this.currentPrice) {
            return this.currentPrice + this.step
        }

        return value - this.step
    }

    private handleInput = () => {
        if (!this.inputController) {
            return
        }

        const input = this.inputController.getInput()
        const previousValue = this.inputController.getValue()

        const newValue = formatPrice(this.extractNumber(previousValue))
        const selectionStartBefore = input?.selectionStart ?? 0
        const nextPos = this.resolveCaretPosition(previousValue, selectionStartBefore, newValue)

        this.inputController.setValue(newValue)
        this.dispatchChange(this.extractNumber(newValue))

        requestAnimationFrame(() => input?.setSelectionRange(nextPos, nextPos))
    }

    private handleKeyDown = (event: KeyboardEvent) => {
        if (!this.inputController) {
            return
        }

        const input = this.inputController.getInput()

        if (!input) {
            return
        }

        const value = input.value
        let position = input.selectionStart ?? 0
        const end = input.selectionEnd ?? position

        if (event.key === 'ArrowUp') {
            event.preventDefault()
            this.setValue(this.increaseValue(this.extractNumber(value)))
            return input.setSelectionRange(position, position)
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault()
            this.setValue(this.decreaseValue(this.extractNumber(value)))
            return input.setSelectionRange(position, position)
        }

        if (event.key === 'ArrowRight') {
            event.preventDefault()
            position = this.moveRight(position, value)
            return input.setSelectionRange(position, position)
        }

        if (event.key === 'ArrowLeft') {
            event.preventDefault()
            position = this.moveLeft(position, value)
            return input.setSelectionRange(position, position)
        }

        if (event.key === 'Home') {
            event.preventDefault()
            position = this.moveRight(0, value)
            return input.setSelectionRange(position, position)
        }

        if (event.key === 'End') {
            event.preventDefault()
            position = this.getEditableMax(value)
            return input.setSelectionRange(position, position)
        }

        if (event.key === 'Backspace') {
            event.preventDefault()

            const { formatted, leftDigits } = this.deleteBackspace(value, position, end)
            this.inputController.setValue(formatted)
            this.dispatchChange(this.extractNumber(formatted))

            const caret = this.caretPosForLeftDigits(leftDigits, formatted)

            return input.setSelectionRange(caret, caret)
        }

        if (event.key === 'Delete') {
            event.preventDefault()

            const { formatted, leftDigits } = this.deleteForward(value, position, end)
            this.inputController.setValue(formatted)
            this.dispatchChange(this.extractNumber(formatted))

            const caret = this.caretPosForLeftDigits(leftDigits, formatted)

            return input.setSelectionRange(caret, caret)
        }
    }

    private handleBlur = () => {
        if (!this.inputController) {
            return
        }

        const entered = this.extractNumber(this.inputController.getValue())
        const adjusted = this.adjustToStep(entered)

        this.setValue(adjusted)
    }

    private dispatchChange(numericValue: number): void {
        this.element.dispatchEvent(
            new CustomEvent('price-input:change', {
                detail: { value: numericValue, formatted: formatPrice(numericValue) },
                bubbles: true
            })
        )

        if (!this.inputController) {
            return
        }

        this.inputController.getInput()?.dispatchEvent(new Event('change', { bubbles: true }))
    }
}
